import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { ErrorBanner, Field, NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import { useTagGroups, useTrackingMutation, type Tag, type TagGroup } from '@/lib/tracking';

export const Route = createFileRoute('/settings/tags')({ component: Tags });

type Editing =
  | { kind: 'tag'; tag: Tag | null; groupId: string | null }
  | { kind: 'group'; group: TagGroup | null }
  | null;

/** Screen L3 (document 06): tags in groups. Tags never touch the ledger. */
function Tags() {
  const t = useT();
  const orgId = useOrgId();
  const me = useMe();
  const isStaff = me.data?.isStaff ?? false;
  const list = useTagGroups('all');
  const [editing, setEditing] = useState<Editing>(null);
  const act = useTrackingMutation(
    (o, v: { path: string; method: 'PATCH' | 'DELETE'; body?: unknown }) =>
      api<unknown>(`/orgs/${o}/${v.path}`, {
        method: v.method,
        ...(v.body ? { body: JSON.stringify(v.body) } : {}),
      }),
  );
  if (!orgId) return <EmptyState title={t('shell.selectClient')} />;

  const rows = (tags: Tag[]) =>
    tags.map((tag) => (
      <TableRow key={tag.id} data-testid={`tag-${tag.name}`}>
        <TableCell className="ps-8">{tag.name}</TableCell>
        <TableCell className="tnum text-end">{tag.uses}</TableCell>
        <TableCell>
          {tag.active ? t('settingsPages.data.tree.active') : t('common.inactive')}
        </TableCell>
        <TableCell>
          {isStaff ? (
            <div className="flex items-center">
              <Button
                variant="link"
                size="sm"
                className="px-1"
                onClick={() => setEditing({ kind: 'tag', tag, groupId: tag.groupId })}
              >
                {t('common.edit')}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t('settingsPages.data.tree.moreActions', { name: tag.name })}
                  >
                    <ChevronDown className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onSelect={() =>
                      act.mutate({
                        path: `tags/${tag.id}`,
                        method: 'PATCH',
                        body: { active: !tag.active },
                      })
                    }
                  >
                    {tag.active
                      ? t('settingsPages.data.tree.makeInactive')
                      : t('settingsPages.data.tree.makeActive')}
                  </DropdownMenuItem>
                  {tag.uses === 0 ? (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        variant="destructive"
                        onSelect={() =>
                          act.mutate(
                            { path: `tags/${tag.id}`, method: 'DELETE' },
                            {
                              onSuccess: () =>
                                toast(t('settingsPages.data.tags.tagDeleted', { name: tag.name })),
                            },
                          )
                        }
                      >
                        {t('settingsPages.data.tree.delete')}
                      </DropdownMenuItem>
                    </>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : null}
        </TableCell>
      </TableRow>
    ));

  return (
    <div>
      <PageHeader
        title={t('settingsPages.data.tags.title')}
        description={t('settingsPages.data.tags.description')}
        actions={
          isStaff ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="tags-new">
                  {t('common.new')} <ChevronDown className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={() => setEditing({ kind: 'tag', tag: null, groupId: null })}
                >
                  {t('settingsPages.data.tags.newTag')}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setEditing({ kind: 'group', group: null })}>
                  {t('settingsPages.data.tags.newGroup')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null
        }
      />
      <ErrorBanner>{act.error?.message ?? null}</ErrorBanner>
      {list.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : list.isError ? (
        <EmptyState title={t('settingsPages.data.tags.couldNotLoad')} hint={list.error.message} />
      ) : !list.data.groups.length && !list.data.ungrouped.length ? (
        <EmptyState
          title={t('settingsPages.data.tags.noneYet')}
          hint={t('settingsPages.data.tags.example')}
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('settingsPages.data.tree.name')}</TableHead>
                <TableHead className="w-[140px] text-end">
                  {t('settingsPages.data.tags.transactions')}
                </TableHead>
                <TableHead className="w-[120px]">{t('settingsPages.data.tree.status')}</TableHead>
                <TableHead className="w-[160px]">{t('settingsPages.data.tree.action')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.data.groups.map((g) => (
                <GroupRows
                  key={g.id}
                  group={g}
                  isStaff={isStaff}
                  onEdit={() => setEditing({ kind: 'group', group: g })}
                  onAddTag={() => setEditing({ kind: 'tag', tag: null, groupId: g.id })}
                  onDelete={() =>
                    act.mutate(
                      { path: `tag-groups/${g.id}`, method: 'DELETE' },
                      {
                        onSuccess: () =>
                          toast(t('settingsPages.data.tags.groupDeleted', { name: g.name })),
                      },
                    )
                  }
                >
                  {rows(g.tags)}
                </GroupRows>
              ))}
              {list.data.ungrouped.length ? (
                <>
                  <TableRow className="bg-muted/30">
                    <TableCell colSpan={4} className="font-medium">
                      {t('settingsPages.data.tags.ungrouped')}
                    </TableCell>
                  </TableRow>
                  {rows(list.data.ungrouped)}
                </>
              ) : null}
            </TableBody>
          </Table>
        </div>
      )}
      <TagDialog
        editing={editing}
        groups={list.data?.groups ?? []}
        onClose={() => setEditing(null)}
      />
    </div>
  );
}

function GroupRows({
  group,
  isStaff,
  onEdit,
  onAddTag,
  onDelete,
  children,
}: {
  group: TagGroup;
  isStaff: boolean;
  onEdit: () => void;
  onAddTag: () => void;
  onDelete: () => void;
  children: React.ReactNode;
}) {
  const t = useT();
  return (
    <>
      <TableRow className="bg-muted/30" data-testid={`tag-group-${group.name}`}>
        <TableCell colSpan={3} className="font-medium">
          {group.name}
        </TableCell>
        <TableCell>
          {isStaff ? (
            <div className="flex items-center">
              <Button variant="link" size="sm" className="px-1" onClick={onAddTag}>
                {t('settingsPages.data.tags.addTag')}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t('settingsPages.data.tree.moreActions', { name: group.name })}
                  >
                    <ChevronDown className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={onEdit}>
                    {t('settingsPages.data.tags.rename')}
                  </DropdownMenuItem>
                  {!group.tags.length ? (
                    <DropdownMenuItem variant="destructive" onSelect={onDelete}>
                      {t('settingsPages.data.tree.delete')}
                    </DropdownMenuItem>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : null}
        </TableCell>
      </TableRow>
      {children}
    </>
  );
}

function TagDialog({
  editing,
  groups,
  onClose,
}: {
  editing: Editing;
  groups: TagGroup[];
  onClose: () => void;
}) {
  const t = useT();
  const [name, setName] = useState('');
  const [groupId, setGroupId] = useState('');
  const [seeded, setSeeded] = useState<Editing>(null);
  if (editing !== seeded) {
    setSeeded(editing);
    setName(
      editing?.kind === 'tag'
        ? (editing.tag?.name ?? '')
        : editing?.kind === 'group'
          ? (editing.group?.name ?? '')
          : '',
    );
    setGroupId(editing?.kind === 'tag' ? (editing.groupId ?? '') : '');
  }
  const save = useTrackingMutation((orgId, e: NonNullable<Editing>) => {
    if (e.kind === 'group')
      return api<unknown>(`/orgs/${orgId}/tag-groups${e.group ? `/${e.group.id}` : ''}`, {
        method: e.group ? 'PATCH' : 'POST',
        body: JSON.stringify({ name: name.trim() }),
      });
    return api<unknown>(`/orgs/${orgId}/tags${e.tag ? `/${e.tag.id}` : ''}`, {
      method: e.tag ? 'PATCH' : 'POST',
      body: JSON.stringify({ name: name.trim(), groupId: groupId || null }),
    });
  });
  const isGroup = editing?.kind === 'group';
  const isNew = editing?.kind === 'group' ? !editing.group : !editing?.tag;
  return (
    <Dialog
      open={editing !== null}
      onOpenChange={(o) => {
        if (!o) {
          save.reset();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isGroup
              ? isNew
                ? t('settingsPages.data.tags.newGroupTitle')
                : t('settingsPages.data.tags.editGroupTitle')
              : isNew
                ? t('settingsPages.data.tags.newTagTitle')
                : t('settingsPages.data.tags.editTagTitle')}
          </DialogTitle>
          <DialogDescription>
            {isGroup
              ? t('settingsPages.data.tags.groupNamesUnique')
              : t('settingsPages.data.tags.tagNamesUnique')}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <ErrorBanner>{save.error?.message ?? null}</ErrorBanner>
          <Field label={t('settingsPages.data.tree.nameRequired')}>
            {(id) => (
              <Input
                id={id}
                value={name}
                maxLength={40}
                onChange={(e) => setName(e.target.value)}
              />
            )}
          </Field>
          {!isGroup ? (
            <Field label={t('settingsPages.data.tags.group')}>
              {(id) => (
                <NativeSelect id={id} value={groupId} onChange={(e) => setGroupId(e.target.value)}>
                  <option value="">{t('settingsPages.data.tags.noGroup')}</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
          ) : null}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              save.reset(); // a failed save's message must not greet the next open
              onClose();
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            disabled={save.isPending || !name.trim()}
            onClick={() =>
              editing &&
              save.mutate(editing, {
                onSuccess: () => {
                  toast(
                    isGroup
                      ? t('settingsPages.data.tags.groupSaved', { name: name.trim() })
                      : t('settingsPages.data.tags.tagSaved', { name: name.trim() }),
                  );
                  onClose();
                },
              })
            }
          >
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
